import { useState } from 'react'
import { ExternalLink, Building, Calendar, MapPin, FileText, GitFork } from 'lucide-react'
import { Drawer } from '@/components/ui/Drawer'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { PoStatusBadge } from '@/features/proto/ProtoBadges'
import type { PurchaseOrder } from '@/services/procurement'
import { money } from '@/services/procurement'
import { deriveDeliveryStatus, formatVnNumber } from './poWorkflow'
import { PoLineTraceabilityModal } from './PoLineTraceabilityModal'
import {
  getPoLineTraceabilityApi,
  type PoLineTraceabilityResult,
} from '@/services/procurement/materialRequirementApiClient'
import {
  poTotal,
  PO_TOTAL_LABEL,
  poReceiveProgress,
  formatReceivePct,
  poHasUnknownVat,
} from '@/features/proto/poListMetrics'

export interface PoPeekDrawerProps {
  open: boolean
  onClose: () => void
  po: PurchaseOrder | null
  supplierName?: string
  onOpenDetail: (poId: string) => void
}

export function PoPeekDrawer({
  open,
  onClose,
  po,
  supplierName,
  onOpenDetail,
}: PoPeekDrawerProps) {
  const [traceModalOpen, setTraceModalOpen] = useState(false)
  const [traceData, setTraceData] = useState<PoLineTraceabilityResult | null>(null)
  const [traceLoading, setTraceLoading] = useState(false)
  const [traceError, setTraceError] = useState<string | null>(null)

  async function handleOpenTraceability(poLineId: string) {
    setTraceModalOpen(true)
    setTraceLoading(true)
    setTraceError(null)
    setTraceData(null)
    try {
      const res = await getPoLineTraceabilityApi(poLineId)
      setTraceData(res)
    } catch (err: any) {
      console.error('Failed to load PO line traceability in peek drawer:', err)
      setTraceError(err?.message || 'Không thể lấy thông tin truy vết cho dòng hàng này')
    } finally {
      setTraceLoading(false)
    }
  }

  if (!po) return null

  const deliveryInfo = deriveDeliveryStatus(po)
  const lines = po.lines || []
  const totalAmount = poTotal(po)
  const receiveProgress = poReceiveProgress(po)
  const receivePctText = formatReceivePct(receiveProgress)

  const totalOrdered = lines.reduce((acc, l) => acc + (l.qty || 0), 0)
  const totalReceived = lines.reduce((acc, l) => acc + (l.receivedQty || 0), 0)
  const totalRemaining = lines.reduce(
    (acc, l) => acc + Math.max(0, (l.qty || 0) - (l.receivedQty || 0)),
    0,
  )

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Xem nhanh đơn mua hàng — ${po.code}`}
      widthClass="w-full max-w-2xl lg:max-w-3xl"
    >
      <div className="space-y-4 text-xs">
        {/* Top Action Bar & Status */}
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <PoStatusBadge status={po.status} />
            <StatusBadge tone={deliveryInfo.tone} label={deliveryInfo.label} />
            {po.archivedAt && (
              <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[11px] text-slate-700">
                Đã lưu trữ
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={onClose}>
              Đóng
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => onOpenDetail(po.id)}
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Mở chi tiết đầy đủ
            </Button>
          </div>
        </div>

        {/* PO Header Information Grid */}
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-white p-3.5 shadow-xs sm:grid-cols-3">
          <div>
            <div className="text-[11px] font-medium text-slate-500 flex items-center gap-1">
              <Building className="h-3 w-3 text-slate-400" />
              Nhà cung cấp
            </div>
            <div className="mt-0.5 font-semibold text-slate-800 truncate" title={supplierName || po.supplierName}>
              {supplierName || po.supplierName || '—'}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-medium text-slate-500 flex items-center gap-1">
              <Calendar className="h-3 w-3 text-slate-400" />
              Ngày giao hàng
            </div>
            <div className="mt-0.5 font-semibold text-slate-800">
              {po.deliveryDate || '—'}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-medium text-slate-500 flex items-center gap-1">
              <MapPin className="h-3 w-3 text-slate-400" />
              Địa chỉ nhận / Xưởng
            </div>
            <div className="mt-0.5 font-semibold text-slate-800 truncate" title={po.deliverySite}>
              {po.deliverySite || '—'}
            </div>
          </div>

          <div className="col-span-2 sm:col-span-3 pt-1 border-t border-slate-100">
            <div className="text-[11px] font-medium text-slate-500 flex items-center gap-1">
              <FileText className="h-3 w-3 text-slate-400" />
              Ghi chú đơn hàng
            </div>
            <div className="mt-0.5 text-slate-700 italic">
              {po.note?.trim() ? po.note : 'Không có ghi chú'}
            </div>
          </div>
        </div>

        {/* Financial & Receiving Metrics */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <div className="rounded border border-slate-200 bg-slate-50 p-2.5 text-center">
            <div className="text-[11px] text-slate-500">{PO_TOTAL_LABEL}</div>
            <div className="text-sm font-bold text-slate-900 mt-0.5">{money(totalAmount)}</div>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-2.5 text-center">
            <div className="text-[11px] text-slate-500">Tiến độ nhận hàng</div>
            <div className="text-sm font-bold text-emerald-700 mt-0.5">{receivePctText}</div>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-2.5 text-center">
            <div className="text-[11px] text-slate-500">Số dòng hàng</div>
            <div className="text-sm font-bold text-slate-900 mt-0.5">{lines.length} dòng</div>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-2.5 text-center">
            <div className="text-[11px] text-slate-500">Trạng thái thuế VAT</div>
            <div className={`text-sm font-bold mt-0.5 ${poHasUnknownVat(po) ? 'text-amber-600' : 'text-slate-900'}`}>
              {poHasUnknownVat(po) ? 'Có UNKNOWN' : 'Đã biết'}
            </div>
          </div>
        </div>

        {/* Lines Section */}
        <div>
          <div className="mb-2 flex items-center justify-between font-semibold text-slate-800">
            <span>Danh sách dòng hàng ({lines.length} dòng)</span>
            <span className="text-[11px] font-normal text-slate-500">Chế độ xem nhanh (Read-Only)</span>
          </div>

          <div className="max-h-72 overflow-auto rounded border border-slate-200">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                <tr>
                  <th className="py-2 px-2 text-center w-10">STT</th>
                  <th className="py-2 px-2 min-w-[140px]">Vật tư</th>
                  <th className="py-2 px-2 min-w-[110px]">Quy cách</th>
                  <th className="py-2 px-2 text-center w-14">ĐVT</th>
                  <th className="py-2 px-2 text-right w-16">Đặt</th>
                  <th className="py-2 px-2 text-right w-16">Đã nhận</th>
                  <th className="py-2 px-2 text-right w-16 text-amber-700">Còn lại</th>
                  <th className="py-2 px-2 text-right w-24">Đơn giá</th>
                  <th className="py-2 px-2 text-right w-24">Thành tiền</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-6 text-center text-slate-400">
                      Không có dòng hàng nào
                    </td>
                  </tr>
                ) : (
                  lines.map((l, index) => {
                    const rem = Math.max(0, (l.qty || 0) - (l.receivedQty || 0))
                    return (
                      <tr key={l.id || index} className="hover:bg-slate-50/50">
                        <td className="py-2 px-2 text-center text-slate-400">{index + 1}</td>
                        <td className="py-2 px-2">
                          <div className="font-medium text-slate-800 flex items-center gap-1.5">
                            <span>{l.itemName}</span>
                            {l.requirementLineId && (
                              <button
                                type="button"
                                onClick={() => void handleOpenTraceability(l.id)}
                                className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 cursor-pointer"
                                title="Xem truy vết nguồn gốc BOM"
                              >
                                <GitFork className="h-2.5 w-2.5" />
                                <span>BOM</span>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-slate-600 font-mono text-[11px]">
                          {l.spec || '—'}
                        </td>
                        <td className="py-2 px-2 text-center text-slate-600">{l.uom}</td>
                        <td className="py-2 px-2 text-right font-mono text-slate-700">
                          {formatVnNumber(l.qty)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-slate-700">
                          {formatVnNumber(l.receivedQty)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono font-semibold text-amber-700">
                          {formatVnNumber(rem)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-slate-700">
                          {money(l.unitPriceActual)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono font-semibold text-slate-900">
                          {money(l.lineTotal)}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Table Total Summary */}
          {lines.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center justify-between rounded bg-slate-50 p-2 font-medium text-slate-700 text-xs border border-slate-200">
              <span>Tổng cộng số lượng:</span>
              <div className="flex gap-4">
                <span>
                  Đặt: <strong>{formatVnNumber(totalOrdered)}</strong>
                </span>
                <span>
                  Đã nhận: <strong>{formatVnNumber(totalReceived)}</strong>
                </span>
                <span>
                  Còn lại: <strong className="text-amber-700">{formatVnNumber(totalRemaining)}</strong>
                </span>
                <span>
                  Tổng tiền: <strong className="text-slate-900">{money(totalAmount)}</strong>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer info note and bottom actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-slate-100">
          <div className="text-[11px] text-slate-500 max-w-sm">
            Xem nhanh thông tin chứng từ. Để chỉnh sửa thông tin, bảng dòng hàng hoặc nhận hàng, vui lòng mở trang chi tiết đầy đủ.
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={onClose}>
              Đóng
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => onOpenDetail(po.id)}
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              Mở chi tiết đầy đủ →
            </Button>
          </div>
        </div>
      </div>

      <PoLineTraceabilityModal
        isOpen={traceModalOpen}
        onClose={() => setTraceModalOpen(false)}
        data={traceData}
        loading={traceLoading}
        error={traceError}
      />
    </Drawer>
  )
}
